import {
  getGridSingleSelectOperators,
  GridColDef,
  GridRenderCellParams,
  GridSingleSelectColDef,
  GridValueFormatter,
} from '@mui/x-data-grid-premium'
import { LeadRow, LeadTableLookupFilterGroup, LookupMap } from "./leadTableTypes"
import { kebabCase } from "lodash-es"
import { Box, Chip } from "@mui/material"
import { ReactNode } from "react"
import { getStatusStyle } from "../components/StatusFilterButton"
import buildLookupFilterOperators from "../filters/buildLookupFilterOperators"


const BOOLEAN_VALUE_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

const BOOLEAN_FILTER_OPERATORS = getGridSingleSelectOperators()
  .filter((operator) => operator.value === 'is')
  .map((operator) => ({
    ...operator,
    label: 'equals',
  }))

type BooleanValueOption = (typeof BOOLEAN_VALUE_OPTIONS)[number]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatCurrencyFromCents: GridValueFormatter<LeadRow, any, any, never> = (value) => {
  if (value == null) {
    return ''
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value) / 100)
}

export function formatDateTime(value: unknown, timeZone: string) {
  if (!value) {
    return ''
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(String(value)))
}

export function renderDateTime(timeZone: string) {
  return function DateTimeCell(params: GridRenderCellParams<LeadRow, string | null>) {
    return (
      <Box sx={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(params.value, timeZone)}
      </Box>
    )
  }
}

export function renderBooleanChip(params: GridRenderCellParams<LeadRow, boolean>) {
  const value = Boolean(params.value)

  return (
    <Chip
      label={value ? 'Yes' : 'No'}
      color={value ? 'success' : 'error'}
      size='small'
      sx={{
        fontSize: '12px',
      }}
    />
  )
}

export function renderStatusChip(status: string | null | undefined) {
  return status ? (
    <Chip
      label={status}
      size='small'
      sx={{
        backgroundColor: getStatusStyle(kebabCase(status)),
        fontSize: '12px',
      }}
    />
  ) : null
}

export function renderHeaderWithFilter({
  title,
  filterNode,
}: {
  title?: string
  filterNode: ReactNode
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {filterNode}
      </Box>
      {title &&
        <Box
          sx={{
            flex: 1,
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            top: '1px',
          }}
        >
          {title}
        </Box>
      }
    </Box>
  )
}

export function buildBooleanDisplayColumn<TField extends keyof LeadRow>(params: {
  field: TField
  headerName: string
  minWidth?: number
  filterable?: boolean
  flex?: number
  renderCell?: (params: GridRenderCellParams<LeadRow, LeadRow[TField]>) => ReactNode
}) {
  const { field, headerName, minWidth = 120, filterable = true, flex = 1, renderCell } = params

  return {
    field,
    headerName,
    minWidth,
    type: 'singleSelect',
    filterable,
    valueOptions: BOOLEAN_VALUE_OPTIONS,
    filterOperators: BOOLEAN_FILTER_OPERATORS,
    getOptionLabel: (option: BooleanValueOption) => option.label,
    getOptionValue: (option: BooleanValueOption) => String(option.value),
    valueFormatter: (value) => (value ? 'Yes' : 'No'),
    renderCell:
      renderCell ??
      ((cellParams: GridRenderCellParams<LeadRow, LeadRow[TField]>) =>
        cellParams.value ? 'Yes' : 'No'),
    align: 'left',
    headerAlign: 'left',
    flex,
    isBooleanColumn: true,
  } satisfies GridSingleSelectColDef<LeadRow> & { isBooleanColumn: true }
}

export function formatLookupValue(value: unknown, lookup: LookupMap) {
  if (value == null || value === '') {
    return ''
  }

  return lookup[String(value)] ?? String(value)
}

export function buildLookupDisplayColumn<TField extends keyof LeadRow>(params: {
  field: TField
  headerName: string
  lookup: LookupMap
  groups?: LeadTableLookupFilterGroup[]
  minWidth?: number
  filterable?: boolean
  flex?: number
  renderCell?: (params: GridRenderCellParams<LeadRow, LeadRow[TField]>) => ReactNode
  filterOperators?: GridSingleSelectColDef<LeadRow>['filterOperators']
}) {
  const {
    field,
    headerName,
    lookup,
    groups = undefined,
    minWidth = 140,
    filterable = true,
    flex = 1,
    renderCell,
    filterOperators,
  } = params

  const valueOptions = Object.entries(lookup)
    .map(([value, label]) => ({
      value,
      label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return {
    field,
    headerName,
    minWidth,
    type: 'singleSelect',
    filterable,
    valueOptions,
    filterOperators:
      filterOperators ??
      buildLookupFilterOperators({
        lookup,
        groups,
        searchable: true,
        showBulkToggles: true,
      }),
    valueFormatter: (value) => formatLookupValue(value, lookup),
    renderCell:
      renderCell ??
      ((cellParams: GridRenderCellParams<LeadRow, LeadRow[TField]>) =>
        formatLookupValue(cellParams.row[field], lookup)),
    align: 'left',
    headerAlign: 'left',
    flex,
  } satisfies GridColDef<LeadRow>
}
